import { getCountryByCode, getCityById } from '../data-source';
import { SITE_NAME, SITE_URL } from '../brand';
import { DEFAULT_OG_IMAGE, toAbsoluteImageUrl } from '../images';
import { toHreflang, isRtlLang } from '../i18n';
import type { Metadata } from 'next';

/** Look up country entity to pass imageUrl to OG helpers */
export function findCountry(code: string) {
  return getCountryByCode(code);
}
/** Look up city entity to pass imageUrl to OG helpers */
export function findCity(id: string) {
  return getCityById(id);
}

/** Escape JSON-LD for safe embedding in <script> tags — prevents XSS via </script> sequences */
export function escapeJsonLd(jsonLd: Record<string, unknown>): string {
  return JSON.stringify(jsonLd).replace(/<\//g, '<\\/');
}

/** Convert alternates array to hreflang languages map with x-default */
export function buildLanguagesMap(alternates: Array<{ lang: string; slug: string }>): Record<string, string> {
  const languages: Record<string, string> = {};
  for (const alt of alternates) {
    languages[toHreflang(alt.lang)] = `${SITE_URL}/${alt.slug}`;
  }
  const enAlt = alternates.find(a => a.lang === 'en');
  if (enAlt) {
    languages['x-default'] = `${SITE_URL}/${enAlt.slug}`;
  }
  return languages;
}

/** Builds a schema.org FAQPage JSON-LD from an array of question/answer pairs */
export function buildFaqPageJsonLd(
  faqs: Array<{ question: string; answer: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
}

/** Map language codes to OpenGraph locale format */
const LANG_TO_OG_LOCALE: Record<string, string> = {
  en: 'en_US', id: 'id_ID', ar: 'ar_SA', th: 'th_TH',
  vi: 'vi_VN', ms: 'ms_MY', tl: 'tl_PH', hi: 'hi_IN',
  my: 'my_MM', mn: 'mn_MN', lo: 'lo_LA', tr: 'tr_TR',
  am: 'am_ET', sd: 'sd_PK', ku: 'ku_IQ', fa: 'fa_IR',
  zh: 'zh_CN', ja: 'ja_JP', ko: 'ko_KR', fr: 'fr_FR',
  de: 'de_DE', es: 'es_ES',
};

function langToOgLocale(lang?: string): string {
  return LANG_TO_OG_LOCALE[lang ?? 'en'] ?? 'en_US';
}

/** Builds Next.js Metadata with consistent OG/Twitter/alternates boilerplate.
 *  Note: `keywords` meta tag intentionally omitted — ignored by all major
 *  search engines since 2009. */
export function buildMetadata(opts: {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogImageAlt?: string;
  languages?: Record<string, string>;
  lang?: string;
  noindex?: boolean;
}): Metadata {
  const { title, description, canonical, languages, lang } = opts;
  const ogImage = opts.ogImage ?? toAbsoluteImageUrl(DEFAULT_OG_IMAGE);
  const ogImageAlt = opts.ogImageAlt ?? description.slice(0, 100);
  const hasLanguages = languages && Object.keys(languages).length > 0;

  // Emit content-language / x-dir meta tags for non-English pages so the
  // root layout inline script can update <html lang> and dir at runtime.
  const otherMeta: Record<string, string> | undefined =
    lang && lang !== 'en'
      ? { 'content-language': lang, 'x-dir': isRtlLang(lang) ? 'rtl' : 'ltr' }
      : undefined;

  return {
    title,
    description,
    ...(opts.noindex && { robots: { index: false, follow: true } }),
    ...(otherMeta && { other: otherMeta }),
    alternates: {
      canonical,
      ...(hasLanguages ? { languages } : {}),
    },
    openGraph: {
      title,
      description,
      url: canonical,
      type: 'website',
      siteName: SITE_NAME,
      locale: langToOgLocale(lang),
      images: [{ url: ogImage, width: 1200, height: 630, alt: ogImageAlt, type: 'image/webp' }],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [{ url: ogImage, alt: ogImageAlt }],
    },
  };
}

/** Builds a BreadcrumbList JSON-LD object.
 *  All items include `item` URL — Google validators flag missing `item` even
 *  on the final breadcrumb. */
export function buildBreadcrumbListJsonLd(
  items: Array<{ name: string; url?: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}
